import { Form, Input, Select } from 'antd';
import type { RuleObject } from 'antd/es/form';
import { useEffect, useMemo } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useCountries } from 'app/hooks/data/useCountries';
import { generateVatMessage, generateVatValidator } from 'app/utils/vat';

import type { FinanceFormData } from '../type';
import { RULES } from '../utils';

const { Option } = Select;

type InvoicingInformationSectionProps = {
  isEditMode: boolean;
  hotelId: number;
};

const FRANCE_COUNTRY_ID = 1;

export const InvoicingInformationSection = ({
  isEditMode,
  hotelId,
}: InvoicingInformationSectionProps) => {
  const { data: hotel } = useGetHotel(hotelId);
  const { data: countries } = useCountries();
  const form = Form.useFormInstance<FinanceFormData>();
  const countryValue = Form.useWatch(['invoicingInformation', 'country'], form);

  const sortedCountries = useMemo(() => {
    return countries
      ? [...countries].sort((a, b) => a.name.localeCompare(b.name))
      : [];
  }, [countries]);

  const invoicingInfo = hotel?.finance?.invoicing;
  const companyName = hotel?.finance?.invoicing?.hotelName;
  const street = invoicingInfo?.street;
  const zipCode = invoicingInfo?.zipCode;
  const city = invoicingInfo?.city;
  const vat = hotel?.finance?.vat;
  const siret = hotel?.siret;

  const fullAddress = [street, zipCode, city].filter(Boolean).join(', ');

  const selectedCountryEdit = countries?.find(
    (c) => c.name === countryValue || c.id === hotel?.countryId
  );
  const shouldDisplaySiretEdit = selectedCountryEdit?.id === FRANCE_COUNTRY_ID;

  useEffect(() => {
    if (!shouldDisplaySiretEdit) {
      form.setFieldsValue({
        invoicingInformation: {
          ...form.getFieldValue('invoicingInformation'),
          siret: undefined,
        },
      });
    }
  }, [shouldDisplaySiretEdit, form]);

  const shouldDisplaySiretView =
    countries && hotel?.countryId
      ? countries.some(
          (c) => c.id === hotel.countryId && c.id === FRANCE_COUNTRY_ID
        )
      : false;

  const vatValidator = useMemo(
    () => generateVatValidator(selectedCountryEdit?.slug, false),
    [selectedCountryEdit?.slug]
  );
  const vatMessage = useMemo(
    () => generateVatMessage(selectedCountryEdit?.slug),
    [selectedCountryEdit?.slug]
  );

  const siretValidator = (_: RuleObject, value?: string) => {
    if (!value) {
      // Field is optional, skip validation if empty
      return Promise.resolve();
    }

    if (/^\d{14}$/.test(value)) {
      return Promise.resolve();
    }

    return Promise.reject('SIRET must be 14 digits');
  };

  const FieldValue = ({
    value,
    emptyText = 'ø empty',
  }: {
    value?: string | null;
    emptyText?: string;
  }) => {
    if (!value) {
      return <span style={{ color: '#B3B3B3' }}>{emptyText}</span>;
    }

    return <span>{value}</span>;
  };

  if (!isEditMode) {
    return (
      <>
        <FormLineLayout label="Company name">
          <FieldValue value={companyName} />
        </FormLineLayout>
        <FormLineLayout label="Address">
          <FieldValue value={fullAddress} />
        </FormLineLayout>
        <FormLineLayout label="VAT number">
          <FieldValue value={vat} />
        </FormLineLayout>
        {shouldDisplaySiretView && siret && (
          <FormLineLayout label="SIRET">
            <FieldValue value={siret} />
          </FormLineLayout>
        )}
      </>
    );
  }

  return (
    <>
      <FormLineLayout label="Company name" isRequired>
        <Form.Item name={['invoicingInformation', 'companyName']} rules={RULES}>
          <Input placeholder="Company name" />
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout label="Street">
        <Form.Item name={['invoicingInformation', 'street']}>
          <Input placeholder="Street address" />
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout label="ZIP Code">
        <Form.Item name={['invoicingInformation', 'zipCode']}>
          <Input placeholder="ZIP code" />
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout label="City">
        <Form.Item name={['invoicingInformation', 'city']}>
          <Input placeholder="City" />
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout label="Country">
        <Form.Item name={['invoicingInformation', 'country']}>
          <Select placeholder="Select country" showSearch>
            {sortedCountries?.map((country) => (
              <Option key={country.id} value={country.name}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout label="VAT number">
        <Form.Item
          name={['invoicingInformation', 'vat']}
          rules={[
            {
              message: vatMessage,
              validator: vatValidator,
            },
          ]}
          getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => {
            return e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          }}
        >
          <Input placeholder="VAT number" />
        </Form.Item>
      </FormLineLayout>
      {shouldDisplaySiretEdit && (
        <FormLineLayout label="SIRET">
          <Form.Item
            name={'siret'}
            rules={[
              { message: 'SIRET must be 14 digits', validator: siretValidator },
            ]}
            getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => {
              return e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            }}
          >
            <Input placeholder="SIRET" maxLength={14} />
          </Form.Item>
        </FormLineLayout>
      )}
    </>
  );
};
